// Procedural, tone-matched cast: one customizable human base + a duck pet.
// Stylized "Sims-ish" proportions, rounded limbs, coded walk/idle cycles.
import * as THREE from "three";

const SKINS = ["#f2c9a0", "#e0a877", "#c68642", "#8d5524", "#5c3a21", "#ffdbac"];
const HAIRS = ["#2b1b0e", "#4a2c14", "#111114", "#6b4423", "#9a9a9a", "#c2410c", "#e5b769"];
const SHIRTS = ["#2f6f5e", "#c05a7d", "#3b5ba5", "#d1cfc7", "#e0a458", "#7c5cbf", "#d64545", "#2a9d8f"];
const PANTS = ["#2a2f3a", "#38414f", "#5b4636", "#1f2937", "#6b7280", "#41506b"];
const SHOES = ["#1a1a1a", "#3a2a1a", "#e5e7eb", "#b45309"];

export const randHumanOpts = () => ({
  skin: SKINS[(Math.random() * SKINS.length) | 0], hair: HAIRS[(Math.random() * HAIRS.length) | 0],
  shirt: SHIRTS[(Math.random() * SHIRTS.length) | 0], pants: PANTS[(Math.random() * PANTS.length) | 0],
  shoes: SHOES[(Math.random() * SHOES.length) | 0], female: Math.random() < 0.5, scale: 0.96 + Math.random() * 0.12,
});

const mat = (color, rough = 0.85, metal = 0.0) => new THREE.MeshStandardMaterial({ color: new THREE.Color(color), roughness: rough, metalness: metal });

// A limb that pivots at its top joint.
function limb(len, radius, material) {
  const g = new THREE.Group();
  const m = new THREE.Mesh(new THREE.CapsuleGeometry(radius, len, 6, 12), material);
  m.position.y = -(len / 2 + radius); m.castShadow = true; g.add(m);
  return g;
}

export function makeHuman(opts = {}) {
  const o = { ...randHumanOpts(), ...opts };
  const skin = mat(o.skin, 0.7), shirt = mat(o.shirt, 0.9), pants = mat(o.pants, 0.85), shoes = mat(o.shoes, 0.6), hair = mat(o.hair, 0.75);
  const root = new THREE.Group();
  const body = new THREE.Group(); body.position.y = 0.92; root.add(body);

  // torso
  const torso = new THREE.Mesh(new THREE.CapsuleGeometry(0.19, 0.34, 6, 14), shirt); torso.position.y = 0.28; torso.scale.set(1.15, 1, 0.72); torso.castShadow = true; body.add(torso);
  const hipMesh = new THREE.Mesh(new THREE.CapsuleGeometry(0.17, 0.1, 4, 12), pants); hipMesh.position.y = 0.04; hipMesh.scale.set(1.1, 1, 0.75); hipMesh.castShadow = true; body.add(hipMesh);
  // neck + head
  const neck = new THREE.Mesh(new THREE.CylinderGeometry(0.06, 0.07, 0.08, 10), skin); neck.position.y = 0.52; body.add(neck);
  const head = new THREE.Group(); head.position.y = 0.66; body.add(head);
  const skull = new THREE.Mesh(new THREE.SphereGeometry(0.16, 20, 18), skin); skull.scale.set(0.92, 1.05, 0.95); skull.castShadow = true; head.add(skull);
  const nose = new THREE.Mesh(new THREE.ConeGeometry(0.028, 0.06, 8), skin); nose.rotation.x = Math.PI / 2; nose.position.set(0, -0.01, 0.16); head.add(nose);
  for (const s of [-1, 1]) { const eye = new THREE.Mesh(new THREE.SphereGeometry(0.022, 10, 10), mat("#20242c", 0.3)); eye.position.set(s * 0.06, 0.02, 0.145); head.add(eye); }
  // hair
  const hairMesh = new THREE.Mesh(new THREE.SphereGeometry(0.172, 18, 16, 0, Math.PI * 2, 0, o.female ? Math.PI * 0.75 : Math.PI * 0.55), hair);
  hairMesh.scale.set(0.98, 1.02, 1.0); hairMesh.position.y = 0.01; head.add(hairMesh);
  if (o.female) { const bun = new THREE.Mesh(new THREE.SphereGeometry(0.09, 12, 12), hair); bun.position.set(0, 0.02, -0.14); head.add(bun); }

  // arms (pivot at shoulder)
  const arms = [];
  for (const s of [-1, 1]) {
    const shoulder = new THREE.Group(); shoulder.position.set(s * 0.24, 0.44, 0); body.add(shoulder);
    const upper = limb(0.2, 0.06, shirt); shoulder.add(upper);
    const elbow = new THREE.Group(); elbow.position.y = -(0.2 + 0.12); upper.add(elbow);
    const fore = limb(0.18, 0.052, skin); elbow.add(fore);
    arms.push({ shoulder, elbow, side: s });
  }
  // legs (pivot at hip)
  const legs = [];
  for (const s of [-1, 1]) {
    const hip = new THREE.Group(); hip.position.set(s * 0.1, 0.02, 0); body.add(hip);
    const thigh = limb(0.24, 0.075, pants); hip.add(thigh);
    const knee = new THREE.Group(); knee.position.y = -(0.24 + 0.15); thigh.add(knee);
    const shin = limb(0.24, 0.06, pants); knee.add(shin);
    const foot = new THREE.Mesh(new THREE.BoxGeometry(0.1, 0.06, 0.2), shoes); foot.position.set(0, -(0.24 + 0.12), 0.05); foot.castShadow = true; knee.add(foot);
    legs.push({ hip, knee, shin, side: s });
  }
  root.scale.setScalar(o.scale || 1);

  let phase = 0;
  const update = (dt, moving, speed = 1) => {
    const target = moving ? 8 * speed : 2.4;
    phase += dt * target;
    const amp = moving ? 1 : 0.12;
    const bob = moving ? Math.abs(Math.sin(phase)) * 0.04 : Math.sin(phase * 0.5) * 0.008;
    body.position.y = 0.92 + bob;
    body.rotation.z = moving ? Math.sin(phase) * 0.04 : 0;
    for (const l of legs) {
      const ph = l.side < 0 ? phase : phase + Math.PI;
      l.hip.rotation.x = Math.sin(ph) * 0.55 * amp;
      l.knee.rotation.x = Math.max(0, -Math.sin(ph)) * 0.9 * amp;
    }
    for (const a of arms) {
      const ph = a.side < 0 ? phase + Math.PI : phase;
      a.shoulder.rotation.x = Math.sin(ph) * 0.45 * amp;
      a.elbow.rotation.x = -Math.max(0, Math.sin(ph)) * 0.3 * amp - (moving ? 0.1 : 0.05);
      if (!moving) a.shoulder.rotation.z = a.side * (0.06 + Math.sin(phase * 0.5) * 0.02);
    }
  };
  return { group: root, update };
}

export function makeDuck(opts = {}) {
  const color = opts.color || "#fbbf24";
  const body = mat(color, 0.6); const beakM = mat("#f97316", 0.5);
  const root = new THREE.Group();
  const core = new THREE.Group(); core.position.y = 0.16; root.add(core);
  const belly = new THREE.Mesh(new THREE.SphereGeometry(0.17, 16, 16), body); belly.scale.set(1, 0.88, 1.25); belly.castShadow = true; core.add(belly);
  const head = new THREE.Group(); head.position.set(0, 0.16, 0.12); core.add(head);
  const skull = new THREE.Mesh(new THREE.SphereGeometry(0.11, 16, 16), body); skull.castShadow = true; head.add(skull);
  const beak = new THREE.Mesh(new THREE.ConeGeometry(0.05, 0.12, 10), beakM); beak.rotation.x = Math.PI / 2; beak.position.set(0, -0.01, 0.14); head.add(beak);
  for (const s of [-1, 1]) { const eye = new THREE.Mesh(new THREE.SphereGeometry(0.018, 10, 10), mat("#111", 0.3)); eye.position.set(s * 0.045, 0.03, 0.09); head.add(eye); }
  const wings = [];
  for (const s of [-1, 1]) { const w = new THREE.Mesh(new THREE.SphereGeometry(0.09, 10, 10), body); w.scale.set(0.4, 0.7, 1); w.position.set(s * 0.16, 0, 0); core.add(w); wings.push({ w, s }); }
  const feet = [];
  for (const s of [-1, 1]) { const f = new THREE.Mesh(new THREE.BoxGeometry(0.06, 0.02, 0.1), beakM); f.position.set(s * 0.06, -0.14, 0.02); core.add(f); feet.push(f); }
  root.scale.setScalar(opts.scale || 1);
  let phase = 0;
  const update = (dt, moving) => {
    phase += dt * (moving ? 10 : 3);
    core.position.y = 0.16 + Math.abs(Math.sin(phase)) * (moving ? 0.03 : 0.01);
    head.rotation.z = Math.sin(phase * 0.5) * 0.06;
    wings.forEach((wg) => (wg.w.rotation.z = wg.s * (0.2 + (moving ? Math.abs(Math.sin(phase)) * 0.4 : Math.sin(phase * 0.5) * 0.1))));
  };
  return { group: root, update };
}
